import { SERVER_FORMAT_DATE_LONG } from 'core/constants';
import { formatDateForServer } from 'core/utils';
import { CustomerExternalNumberType } from 'customer/enums';
import { QuoteEntityType } from 'quotes/enums';

import { ShipSelection } from './ShipSelectionDateFields/enums';
import { ICustomerQuoteUpsertForm } from './types';

export const mapCreateCustomerQuoteForm = (
  data: ICustomerQuoteUpsertForm,
  entityType = QuoteEntityType.QUOTE,
  isAlternateVersionEnabled = false
) => {
  const baseQuote = {
    projectId: data.project.id,
    jobName: data.jobName,
    contactId: data.contact.id,
    verticalMarketId: data.verticalMarket?.id,
    locationId: data.location.id,
    shippingLocationId: data.shippingLocation?.id,
    shipToContactGroupAddressId: data.shipToLocation.id,
    billToContactGroupAddressId: data.billToLocation.id,
  };

  const customFields = getCustomFieldsForCreateCustomerQuoteForm(
    data,
    entityType,
    isAlternateVersionEnabled
  );

  // Both flags sit at the payload ROOT, siblings of `quote` — the BE reads them off the request
  // root, not out of `getParam('quote')`. Omitted when unticked to keep the payload clean.
  return {
    quote: {
      ...baseQuote,
      ...customFields,
    },
    ...(data?.setBillToAsDefaultForContact ? { setBillToAsDefaultForContact: 1 } : {}),
    ...(data?.setShipToAsDefaultForContact ? { setShipToAsDefaultForContact: 1 } : {}),
  };
};

export const mapUpdateCustomerQuoteForm = (
  id: number,
  data: ICustomerQuoteUpsertForm,
  entityType = QuoteEntityType.QUOTE,
  isAlternateVersionEnabled = false
) => {
  const baseQuote = {
    id,
    verticalMarketId: data?.verticalMarket?.id,
    shippingLocationId: data?.shippingLocation?.id,
  };

  const customFields = getCustomFieldsForUpdateCustomerQuoteForm(
    data,
    entityType,
    isAlternateVersionEnabled
  );

  return {
    quote: {
      ...baseQuote,
      ...customFields,
    },
  };
};

const getCustomFieldsForCreateCustomerQuoteForm = (
  data: ICustomerQuoteUpsertForm,
  entityType: QuoteEntityType,
  isAlternateVersionEnabled: boolean
) => {
  switch (entityType) {
    case QuoteEntityType.ORDER:
      return {
        isOrder: 1,
        externalCustomerNumber: data?.customerNumber?.externalCustomerNumber,
        externalCustomerNumberType: data?.customerNumberType || CustomerExternalNumberType.ORDER,
        purchaseOrderNumber: data?.purchaseOrderNumber,
        ...(isAlternateVersionEnabled
          ? {
              orderType: data?.orderType,
              pricingDate:
                formatDateForServer(data?.pricingDate?.toString(), SERVER_FORMAT_DATE_LONG) || null,
              pricingStrategy: data?.pricingStrategy || null,
              shipSelection: data?.shipSelection,
              // NDD earliest is API-owned (submission day); the BE ignores anything sent.
              earliestAcceptableDate:
                data?.shipSelection === ShipSelection.NO_DELIVERY_DATE
                  ? ''
                  : formatDateForServer(
                      data?.earliestAcceptableDate?.toString(),
                      SERVER_FORMAT_DATE_LONG
                    ),
              requiredOnSiteDate: formatDateForServer(
                data?.requiredOnSiteDate?.toString(),
                SERVER_FORMAT_DATE_LONG
              ),
              shippingMethodId: data?.shippingMethod?.id || null,
              freightTermId: data?.freightTerm?.id || null,
            }
          : {}),
      };
    case QuoteEntityType.QUOTE:
    default:
      return {
        closeConfidence: data?.closeConfidence,
        estimatedCloseDate: data?.closeDate,
        ...(isAlternateVersionEnabled
          ? {
              pricingDate:
                formatDateForServer(data?.pricingDate?.toString(), SERVER_FORMAT_DATE_LONG) || null,
              pricingStrategy: data?.pricingStrategy || null,
            }
          : {}),
        startDate: formatDateForServer(data?.validSince?.toString(), SERVER_FORMAT_DATE_LONG),
        validUntil: formatDateForServer(data?.validUntil?.toString(), SERVER_FORMAT_DATE_LONG),
      };
  }
};

const getCustomFieldsForUpdateCustomerQuoteForm = (
  data: ICustomerQuoteUpsertForm,
  entityType: QuoteEntityType,
  isAlternateVersionEnabled: boolean
) => {
  const extraFields = {};
  if (data?.closeDate) {
    extraFields['estimatedCloseDate'] = data?.closeDate;
  }

  switch (entityType) {
    case QuoteEntityType.ORDER:
      return {
        externalCustomerNumber: data?.customerNumber?.externalCustomerNumber,
        externalCustomerNumberType: data?.customerNumberType || CustomerExternalNumberType.ORDER,
        purchaseOrderNumber: data?.purchaseOrderNumber,
        ...(isAlternateVersionEnabled
          ? {
              orderType: data?.orderType,
              pricingDate:
                formatDateForServer(data?.pricingDate?.toString(), SERVER_FORMAT_DATE_LONG) || null,
              pricingStrategy: data?.pricingStrategy || null,
              shipSelection: data?.shipSelection,
              // NDD earliest is API-owned (submission day); the BE ignores anything sent.
              earliestAcceptableDate:
                data?.shipSelection === ShipSelection.NO_DELIVERY_DATE
                  ? ''
                  : formatDateForServer(
                      data?.earliestAcceptableDate?.toString(),
                      SERVER_FORMAT_DATE_LONG
                    ),
              requiredOnSiteDate: formatDateForServer(
                data?.requiredOnSiteDate?.toString(),
                SERVER_FORMAT_DATE_LONG
              ),
              shippingMethodId: data?.shippingMethod?.id || null,
              freightTermId: data?.freightTerm?.id || null,
            }
          : {}),
      };
    case QuoteEntityType.QUOTE:
    default:
      return {
        closeConfidence: data?.closeConfidence,
        pricingDate:
          formatDateForServer(data?.pricingDate?.toString(), SERVER_FORMAT_DATE_LONG) || null,
        ...(isAlternateVersionEnabled ? { pricingStrategy: data?.pricingStrategy || null } : {}),
        status: data?.status,
        statusNotes: {
          lost: data?.quoteStatusLostNote,
        },
        startDate: formatDateForServer(data?.validSince?.toString(), SERVER_FORMAT_DATE_LONG),
        validUntil: formatDateForServer(data?.validUntil?.toString(), SERVER_FORMAT_DATE_LONG),
        ...extraFields,
      };
  }
};
